import { useEffect } from "react";
import useCustomQueryClient from "data/modules/api/hooks/useQueryClient";
import { useMarkInboxRead } from "../../../apis/markInboxRead";
import {
  getContactsListQueryKeys,
  getInboxNotificationsCountQueryKeys,
} from "../../../apis/inboxQueryKeys";
import { useContactFiltersStore } from "../../ContactsList/ContactFilters/store/useContactFiltersStore";
import { getContactsListFiltersParams } from "../../ContactsList/ContactsList.utils";

/**
 * Custom hook to mark conversations as read.
 *
 * This hook ensures that a conversation is marked as read in the following scenarios:
 * 1. When a new conversation is opened (triggered by `entityId` change).
 * 2. Every 5 minutes while the conversation remains open.
 * 3. When the component using this hook unmounts.
 *
 * @param {Object} params
 * @param {string | number} params.entityId - The ID of the conversation entity to be marked as read.
 */
export const useMarkConversationsAsRead = ({ entityId }) => {
  const markRead = useMarkInboxRead();
  const queryClient = useCustomQueryClient();
  const unreadApplied = useContactFiltersStore((state) => state.unreadApplied);
  const openApplied = useContactFiltersStore((state) => state.openApplied);
  const selectedOrgIds = useContactFiltersStore(
    (state) => state.selectedOrgIds
  );

  const markConversationAsRead = (entityId, invalidate = false) => {
    if (entityId)
      markRead.mutate(
        { lead_id: entityId },
        {
          onSuccess: () => {
            if (invalidate) {
              queryClient.invalidateQueries({
                queryKey:
                  getInboxNotificationsCountQueryKeys.notificationsCount,
              });
              const params = getContactsListFiltersParams(
                unreadApplied,
                openApplied,
                selectedOrgIds
              );
              queryClient.invalidateQueries({
                queryKey: getContactsListQueryKeys(params).contactsList,
              });
            }
          },
        }
      );
  };

  useEffect(() => {
    if (!entityId) return;
    // marking conversation as read everytime a new conversation is opened
    // added delay of 1 second for better user experience
    setTimeout(() => markConversationAsRead(entityId, true), 1000);

    // marking conversation as read every 5 minutes for open conversation
    const interval = setInterval(() => {
      markConversationAsRead(entityId, true);
    }, 5 * 60 * 1000); // 5 minutes

    return () => {
      // marking conversation as read on unmount
      markConversationAsRead(entityId, true);
      clearInterval(interval);
    };
  }, [entityId]);

  return { markConversationAsRead };
};
